import React, { useState } from "react";
import { View, StyleSheet, TouchableOpacity } from "react-native";

import RText from "../RText";
import Icon from "../Icon";
import { favoritesMapper, prepareParams } from "../Screens/Results/utils";
import { useResponsive } from "../../hooks/useResponsive";
import variables from "../../styles/variables";
import { RaceResult } from "../../types/result";
import { cleanSlug } from "../../utils/cleanSlug";
import { formatRaceTime } from "../../utils/TimeCalculator";
import { getRunnerFinishText } from "../../utils/getRunnerFinishText";
import { isNullified } from "../../utils/race";
import usePressableProps from "../../hooks/usePressableProps";
import VideoLink from "../VideoLink/VideoLink";
import { useAtom } from "jotai";
import { priceFormatAtom } from "../../atoms";
import { Track } from "../../types/races/racecard";
import { Meeting } from "../../types/races/meeting";

type ResultDetailsProps = {
  showTrackName?: boolean;
  race: RaceResult;
  meeting: Meeting;
  track: Track;
  horizontalMargin?: boolean;
  idx?: number;
};

export default function ResultDetails({
  showTrackName = true,
  meeting,
  race,
  track,
  horizontalMargin = true,
  idx = 1,
}: ResultDetailsProps) {
  const { isLargeAndUp, isSmallAndDown, isXSmallOnly } = useResponsive();
  const [hover, setHover] = useState(false);

  const [oddsFormat] = useAtom(priceFormatAtom);

  const params = prepareParams({
    date: meeting.date,
    time: race.start_time_scheduled,
    track: cleanSlug(track.slug),
  });

  const pressablePropsIndividualResult = usePressableProps({
    screen: "IndividualResult",
    params: params,
  });

  if (isNullified(race.status.state)) {
    return (
      <TouchableOpacity
        style={[styles.abandonedCardContainer, { borderBottomWidth: 1 }]}
        {...pressablePropsIndividualResult}
      >
        <RText
          weight="semiBold"
          color="grey"
          style={!isLargeAndUp && { marginHorizontal: 10 }}
        >
          {formatRaceTime(race.start_time_scheduled)}{" "}
        </RText>
        <RText
          weight="bold"
          color="grey"
          uppercase
          style={{ marginHorizontal: 10 }}
        >
          {track?.name?.capitalize()}
        </RText>
        <RText color="grey" uppercase>
          {race.status.state}
        </RText>
      </TouchableOpacity>
    );
  }

  const runnerCount = race.placed_runners.length;

  let previousRunnerFinishText = "";
  const isDeadHeat = (currentRunnerFinishText: string): boolean => {
    return currentRunnerFinishText === previousRunnerFinishText;
  };

  race.placed_runners = race.placed_runners.map((runner, index) => {
    if (isDeadHeat(runner.finish_text)) {
      runner.dead_heat = true;
      race.placed_runners[index - 1].dead_heat = true;
    }
    previousRunnerFinishText = runner.finish_text;
    return runner;
  });

  const formattedRaceTitle = (title: string) => {
    if (!title) {
      return "";
    }
    const splitTitle = title.split(" ");
    for (let section of splitTitle) {
      if (section.includes("(") && section.includes(")")) {
        const index = splitTitle.indexOf(section);
        // section has a number between brackets
        const whatsInTheBrackets = parseInt(section.replace(/\D/g, ""));
        if (!isNaN(whatsInTheBrackets)) {
          splitTitle[index] = `(Class ${whatsInTheBrackets})`;
        }
      }
    }
    return splitTitle.join(" ");
  };

  return (
    <View
      style={[
        styles.raceDetailsContainer,
        idx !== 0 && styles.topPadding,
        !isLargeAndUp &&
          horizontalMargin && {
            paddingHorizontal: 10,
          },
      ]}
    >
      <TouchableOpacity
        {...pressablePropsIndividualResult}
        style={[
          styles.raceDetailsLeftContainer,
          { flex: isLargeAndUp ? 6 : 3 },
        ]}
      >
        <RText family="secondary" weight="bold" color="secondary">
          {formatRaceTime(race.start_time_scheduled)}{" "}
          {showTrackName && track?.name}
        </RText>
        {isLargeAndUp && (
          <RText size="md" family="primary">
            {formattedRaceTitle(race.title).capitalize()}
          </RText>
        )}
        <View
          style={[
            styles.thirdRow,
            !isLargeAndUp && { flexDirection: "column" },
          ]}
        >
          {race.placed_runners?.map((runner, index) => {
            return (
              <RText
                size={isSmallAndDown ? "sm" : "md"}
                weight={runner?.finish_text === "1st" ? "bold" : "regular"}
                color={runner?.finish_text === "1st" ? "black" : null}
                key={`runner-result-${index}`}
              >
                {getRunnerFinishText(
                  runner?.finish_position,
                  runner?.finish_text,
                  runner.dead_heat
                )}
                {runner.horse.name}
                {!!runner.starting_price &&
                  ` ${runner.starting_price[oddsFormat]}${
                    favoritesMapper[runner.starting_price_favorite] || ""
                  }`}
                {index !== runnerCount - 1 && isLargeAndUp && ", "}
              </RText>
            );
          })}
        </View>
      </TouchableOpacity>

      <View
        style={[
          styles.raceDetailsRightContainer,
          { justifyContent: "flex-end" },
        ]}
      >
        {race.status?.state === "resulted" && race.placed_runners?.length ? (
          <>
            <VideoLink
              race={race}
              trackSlug={track?.slug}
              videoType={"replay"}
              shouldBecomeAvailable={
                track?.tv_channel === "RTV" ||
                track?.tv_channel === "SSR" ||
                track?.tv_channel === "ATR"
              }
            ></VideoLink>

            <TouchableOpacity
              // @ts-ignore
              onMouseEnter={() => setHover(true)}
              onMouseLeave={() => setHover(false)}
              {...pressablePropsIndividualResult}
              style={[
                hover && styles.hover,
                {
                  flexDirection: "row",
                },
              ]}
            >
              {!isXSmallOnly && <RText family="primary">Full Result</RText>}
              <Icon name="chevronRightSmall" size="medium" />
            </TouchableOpacity>
          </>
        ) : (
          <RText family="secondary" weight="light">
            Awaiting full result...
          </RText>
        )}
      </View>
    </View>
  );
}

const { colors } = variables;
const styles = StyleSheet.create({
  raceDetailsContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    paddingBottom: 15,
    borderBottomWidth: 1,
    borderBottomColor: colors.palette.neutral.x100,
  },
  abandonedCardContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
    paddingBottom: 15,
    alignItems: "center",
    borderBottomWidth: 1,
    borderBottomColor: colors.palette.neutral.x100,
  },
  topPadding: {
    paddingTop: 15,
  },
  raceDetailsLeftContainer: {
    justifyContent: "flex-start",
  },
  raceDetailsRightContainer: {
    flex: 2,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  thirdRow: {
    flexDirection: "row",
  },
  hover: {
    transform: [{ scale: 1.1 }],
  },
});
